import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import type {
  AnalysisSnapshot,
  AnalysisSnapshotDoc,
  AnalysisSnapshotMetadata,
  AnalysisTimeRangeKey,
} from '../types/analysis'
export type { AnalysisSnapshotMetadata }

export const ANALYSIS_SNAPSHOT_RETENTION_DAYS = 90

const getHistoryCollectionRef = (workspaceId: string) =>
  collection(firestoreDb, 'workspaces', workspaceId, 'analysisSnapshots')

export interface WriteSnapshotParams {
  workspaceId: string
  authorId: string
  timeRange: AnalysisTimeRangeKey
  snapshot: AnalysisSnapshot
}

export interface ListRecentSnapshotsParams {
  workspaceId: string
  limit?: number
}

export interface SubscribeRecentSnapshotsParams {
  workspaceId: string
  limit?: number
  onChange: (snapshots: AnalysisSnapshotMetadata[]) => void
  onError?: (error: Error) => void
}

export interface GetSnapshotParams {
  workspaceId: string
  snapshotId: string
}

export interface SubscribeSnapshotParams extends GetSnapshotParams {
  onChange: (snapshot: AnalysisSnapshotDoc | null) => void
  onError?: (error: Error) => void
}

const parseTimestamp = (value: unknown): Date => {
  if (!value) return new Date()
  if (value instanceof Date) return value
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate()
  }
  const date = new Date(value as string | number)
  return Number.isNaN(date.getTime()) ? new Date() : date
}

const normalizeSnapshotDoc = (snapshotId: string, workspaceId: string, data: Record<string, unknown>): AnalysisSnapshotDoc => ({
  snapshotId,
  workspaceId: typeof data.workspaceId === 'string' ? data.workspaceId : workspaceId,
  authorId: typeof data.authorId === 'string' ? data.authorId : '',
  generatedAt: parseTimestamp(data.generatedAt),
  deletionTimestamp: parseTimestamp(data.deletionTimestamp),
  timeRange: data.timeRange as AnalysisTimeRangeKey,
  health: data.health as AnalysisSnapshotDoc['health'],
  work: data.work as AnalysisSnapshotDoc['work'],
  team: data.team as AnalysisSnapshotDoc['team'],
  ops: data.ops as AnalysisSnapshotDoc['ops'],
  governance: data.governance as AnalysisSnapshotDoc['governance'],
  ai: data.ai as AnalysisSnapshotDoc['ai'],
  aiSummary: typeof data.aiSummary === 'string' ? data.aiSummary : undefined,
  recommendations: Array.isArray(data.recommendations) ? data.recommendations : undefined,
  driftEvents: Array.isArray(data.driftEvents) ? data.driftEvents : undefined,
  blockerChains: Array.isArray(data.blockerChains) ? data.blockerChains : undefined,
})

export const analysisSnapshotService = {
  async writeSnapshot({ workspaceId, authorId, timeRange, snapshot }: WriteSnapshotParams): Promise<AnalysisSnapshotDoc> {
    const now = new Date()
    const deletionDate = new Date(now.getTime() + ANALYSIS_SNAPSHOT_RETENTION_DAYS * 24 * 60 * 60 * 1000)

    const payload = {
      workspaceId,
      authorId,
      timeRange,
      generatedAt: Timestamp.fromDate(now),
      deletionTimestamp: Timestamp.fromDate(deletionDate),
      health: snapshot.health,
      work: snapshot.work,
      team: {
        assigneeLoad: snapshot.team?.assigneeLoad || [],
        unassigned: snapshot.team?.unassigned || 0,
      },
      ops: snapshot.ops,
      governance: snapshot.governance,
      ai: snapshot.ai,
      recommendations: snapshot.recommendations.slice(0, 3).map(({ title, reason, priority }) => ({ title, reason, priority })),
      driftEvents: snapshot.driftEvents.slice(0, 3).map(({ title, driftDays, severity }) => ({ title, driftDays, severity })),
      blockerChains: snapshot.blockerChains.slice(0, 3).map(({ title, chainLength }) => ({ title, chainLength })),
    }

    const colRef = getHistoryCollectionRef(workspaceId)
    const docRef = await addDoc(colRef, payload)

    return {
      snapshotId: docRef.id,
      workspaceId,
      authorId,
      generatedAt: now,
      deletionTimestamp: deletionDate,
      timeRange,
      health: snapshot.health,
      work: snapshot.work,
      team: {
        assigneeLoad: snapshot.team?.assigneeLoad || [],
        unassigned: snapshot.team?.unassigned || 0,
      },
      ops: snapshot.ops,
      governance: snapshot.governance,
      ai: snapshot.ai,
      recommendations: snapshot.recommendations.slice(0, 3).map(({ title, reason, priority }) => ({ title, reason, priority })),
      driftEvents: snapshot.driftEvents.slice(0, 3).map(({ title, driftDays, severity }) => ({ title, driftDays, severity })),
      blockerChains: snapshot.blockerChains.slice(0, 3).map(({ title, chainLength }) => ({ title, chainLength })),
    }
  },

  async listRecentSnapshots({ workspaceId, limit: fetchLimit = 10 }: ListRecentSnapshotsParams): Promise<AnalysisSnapshotMetadata[]> {
    const colRef = getHistoryCollectionRef(workspaceId)
    const q = query(colRef, orderBy('generatedAt', 'desc'), limit(fetchLimit))
    const snap = await getDocs(q)

    return snap.docs.map((docSnap) => {
      const data = docSnap.data()
      const generatedAt = parseTimestamp(data.generatedAt)
      return {
        snapshotId: docSnap.id,
        generatedAt,
        authorId: data.authorId,
        timeRange: data.timeRange,
        healthScore: data.health?.score ?? 0,
        healthState: data.health?.state ?? 'HEALTHY',
      }
    })
  },

  subscribeRecentSnapshots({ workspaceId, limit: fetchLimit = 10, onChange, onError }: SubscribeRecentSnapshotsParams): Unsubscribe {
    const colRef = getHistoryCollectionRef(workspaceId)
    const q = query(colRef, orderBy('generatedAt', 'desc'), limit(fetchLimit))

    return onSnapshot(
      q,
      (snap) => {
        const items: AnalysisSnapshotMetadata[] = snap.docs.map((docSnap) => {
          const data = docSnap.data()
          const generatedAt = parseTimestamp(data.generatedAt)
          return {
            snapshotId: docSnap.id,
            generatedAt,
            authorId: data.authorId,
            timeRange: data.timeRange,
            healthScore: data.health?.score ?? 0,
            healthState: data.health?.state ?? 'HEALTHY',
          }
        })
        onChange(items)
      },
      (err) => {
        if (onError) onError(err)
      }
    )
  },

  async getSnapshot({ workspaceId, snapshotId }: GetSnapshotParams): Promise<AnalysisSnapshotDoc | null> {
    const docRef = doc(firestoreDb, 'workspaces', workspaceId, 'analysisSnapshots', snapshotId)
    const snap = await getDoc(docRef)
    if (!snap.exists()) return null

    return normalizeSnapshotDoc(snap.id, workspaceId, snap.data())
  },

  subscribeSnapshot({ workspaceId, snapshotId, onChange, onError }: SubscribeSnapshotParams): Unsubscribe {
    const docRef = doc(firestoreDb, 'workspaces', workspaceId, 'analysisSnapshots', snapshotId)
    return onSnapshot(docRef, (snap) => {
      onChange(snap.exists() ? normalizeSnapshotDoc(snap.id, workspaceId, snap.data()) : null)
    }, (error) => onError?.(error))
  },
}
